import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@rentflowdev/ui'
import {
	CURRENCY_SIGNS, DEFAULT_CURRENCY, DEFAULT_LOCALE,
	UTILITY_CALCULATION_KEYS, UTILITY_PAID_BY_KEYS, UTILITY_TYPE_KEYS,
} from '@/types'
import type {
	Property, PropertyAdditionalService, PropertyUtility, Rental, RentTerms,
	UtilityCalculation, UtilityPaidBy, UtilityType,
} from '@/types'
import { useGetPropertyQuery } from '@/api'
import { formatVatTerm } from '@/lib/vat-label'

const ACCORDION_SECTIONS = ['basic', 'utilities', 'additional'] as const

function formatCurrency(amount: number, currency: string): string {
	const sign = CURRENCY_SIGNS[currency] ?? currency
	return `${amount.toLocaleString(DEFAULT_LOCALE)}${sign}`
}

interface RentalTermsSectionProps {
	rental: Rental
}

function BasicTermsFields({ terms, t }: { terms: RentTerms; t: (k: string, opts?: Record<string, unknown>) => string }) {
	const period = terms.rentPeriod.toLowerCase()
	return (
		<div className="grid grid-cols-[200px_1fr] gap-x-4 gap-y-3">
			<span className="text-sm text-(--color-muted-foreground)">
				{t('rent.terms.basic.rentAmount')}:
			</span>
			<span className="text-sm text-(--color-foreground)">
				{formatCurrency(Number(terms.rentAmount), terms.rentCurrency)} {t(`rent.periods.${period}`).startsWith('в ')
					? t(`rent.periods.${period}`)
					: `в ${t(`rent.periods.${period}`)}`}
			</span>

			{terms.vatPercent != null && (
				<>
					<span className="text-sm text-(--color-muted-foreground)">{t('rent.terms.basic.vat')}:</span>
					<span className="text-sm text-(--color-foreground)">{formatVatTerm(terms, t)}</span>
				</>
			)}

			{terms.depositAmount != null && (
				<>
					<span className="text-sm text-(--color-muted-foreground)">{t('rent.terms.basic.deposit')}:</span>
					<span className="text-sm text-(--color-foreground)">
						{formatCurrency(Number(terms.depositAmount), terms.rentCurrency)}
					</span>
				</>
			)}

			{terms.penaltyRate != null && (
				<>
					<span className="text-sm text-(--color-muted-foreground)">{t('rent.terms.basic.penalty')}:</span>
					<span className="text-sm text-(--color-foreground)">
						{terms.penaltyRate}% {t('rent.terms.basic.perDay')}
						{terms.penaltyGraceDays != null && `, ${t('rent.terms.basic.gracePeriod', { count: terms.penaltyGraceDays, defaultValue: `льготный период ${terms.penaltyGraceDays} дня` })}`}
					</span>
				</>
			)}
		</div>
	)
}

function UtilityLine({ utility, t }: { utility: PropertyUtility; t: (k: string, opts?: Record<string, unknown>) => string }) {
	const calcLabel = t(UTILITY_CALCULATION_KEYS[utility.calculation])
	const paidByLabel = utility.paidBy ? `, ${t(UTILITY_PAID_BY_KEYS[utility.paidBy])}` : ''
	const value = utility.calculation === 'INCLUDED'
		? t('rent.terms.calc.included')
		: `${calcLabel}${paidByLabel}`

	return (
		<>
			<span className="text-sm text-(--color-muted-foreground)">
				{t(UTILITY_TYPE_KEYS[utility.type])}:
			</span>
			<span className="text-sm text-(--color-foreground)">{value}</span>
		</>
	)
}

function AdditionalServiceLine({ service }: { service: PropertyAdditionalService }) {
	return (
		<>
			<span className="text-sm text-(--color-muted-foreground)">{service.name}:</span>
			<span className="text-sm text-(--color-foreground)">
				{formatCurrency(Number(service.pricePerUnit), service.currency)} × {service.quantity} {service.unit}
			</span>
		</>
	)
}

/** The signed rental's own terms are the source of truth for the basic terms —
 *  they can diverge from the property's listing terms (negotiated rent, renewals).
 *  Rentals signed before rental-level terms existed have null vat/deposit/penalty:
 *  fall back to the property's listing terms for display (the previous source). */
function toRentTerms(rental: Rental, property: Property | undefined): RentTerms {
	// Penalty rate and grace days travel together — never mix the two sources.
	const hasOwnPenalty = rental.penaltyRate != null
	// So do the rate and its grounds: the property fallback is the old
	// hand-entered field, which carries neither a reason nor a split, and
	// pairing it with the rental's would describe terms that were never agreed.
	const hasOwnVat = rental.vatPercent != null
	return {
		id: rental.id,
		propertyId: rental.propertyId,
		rentAmount: rental.rentAmount,
		rentCurrency: rental.currency ?? DEFAULT_CURRENCY,
		vatPercent: rental.vatPercent ?? property?.vatPercent,
		vatIncluded: hasOwnVat ? rental.vatIncluded : undefined,
		vatReason: hasOwnVat ? rental.vatReason : undefined,
		depositAmount: rental.depositAmount ?? property?.depositAmount,
		penaltyRate: hasOwnPenalty ? rental.penaltyRate : property?.penaltyRate,
		penaltyGraceDays: hasOwnPenalty ? rental.penaltyGraceDays : property?.penaltyGraceDays,
		rentPeriod: rental.period ?? 'MONTHLY',
	}
}

function toUtilities(property: Property | undefined): PropertyUtility[] {
	if (!property?.utilities) return []
	return property.utilities.map((u, idx) => ({
		id: u.id ?? `${property.id}-util-${idx}`,
		propertyId: property.id,
		type: u.type as UtilityType,
		calculation: (typeof u.calculation === 'string' ? u.calculation.toUpperCase() : u.calculation) as UtilityCalculation,
		paidBy: (typeof u.paidBy === 'string' ? u.paidBy.toUpperCase() : u.paidBy) as UtilityPaidBy,
		hasSeparateMeter: u.hasSeparateMeter,
		meterNumber: u.meterNumber,
		currency: u.currency ?? DEFAULT_CURRENCY,
	}))
}

function toAdditionalServices(property: Property | undefined): PropertyAdditionalService[] {
	if (!property?.additionalServices) return []
	return property.additionalServices.map((s) => ({
		id: s.id,
		propertyId: property.id,
		name: s.name,
		pricePerUnit: s.pricePerUnit,
		currency: s.currency,
		quantity: s.quantity,
		unit: s.unit,
		provisionType: s.provisionType,
		vatIncluded: s.vatIncluded,
	}))
}

export function RentalTermsSection({ rental }: RentalTermsSectionProps) {
	const { t } = useTranslation()
	const { data } = useGetPropertyQuery(rental.propertyId)
	const property = data ?? undefined

	const terms = useMemo(() => toRentTerms(rental, property), [rental, property])
	const utilities = useMemo(() => toUtilities(property), [property])
	const additional = useMemo(() => toAdditionalServices(property), [property])
	const hasDetailedTerms =
		terms.vatPercent != null || terms.depositAmount != null || terms.penaltyRate != null

	return (
		<Accordion type="multiple" defaultValue={[...ACCORDION_SECTIONS]} className="flex flex-col gap-3">
			<AccordionItem value="basic" className="rounded-(--radius-md) border border-(--color-border) px-4">
				<AccordionTrigger className="py-4 text-base font-[500]">
					{t('rent.terms.basic.title')}
				</AccordionTrigger>
				<AccordionContent className="pb-4">
					<BasicTermsFields terms={terms} t={t} />
					{!hasDetailedTerms && (
						<p className="mt-3 text-sm text-(--color-muted-foreground)">{t('rent.terms.basic.empty')}</p>
					)}
				</AccordionContent>
			</AccordionItem>

			<AccordionItem value="utilities" className="rounded-(--radius-md) border border-(--color-border) px-4">
				<AccordionTrigger className="py-4 text-base font-[500]">
					{t('rent.terms.utilities.title')}
				</AccordionTrigger>
				<AccordionContent className="pb-4">
					{utilities.length === 0 ? (
						<p className="text-sm text-(--color-muted-foreground)">{t('rent.terms.utilities.empty')}</p>
					) : (
						<div className="grid grid-cols-[200px_1fr] gap-x-4 gap-y-3">
							{utilities.map((u) => <UtilityLine key={u.id} utility={u} t={t} />)}
						</div>
					)}
				</AccordionContent>
			</AccordionItem>

			<AccordionItem value="additional" className="rounded-(--radius-md) border border-(--color-border) px-4">
				<AccordionTrigger className="py-4 text-base font-[500]">
					{t('rent.terms.additional.title')}
				</AccordionTrigger>
				<AccordionContent className="pb-4">
					{additional.length === 0 ? (
						<p className="text-sm text-(--color-muted-foreground)">{t('rent.terms.additional.empty')}</p>
					) : (
						<div className="grid grid-cols-[200px_1fr] gap-x-4 gap-y-3">
							{additional.map((s) => <AdditionalServiceLine key={s.id} service={s} />)}
						</div>
					)}
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	)
}
